/**
 * Tipos e estados fiscais comuns.
 *
 * Fonte normativa: Plano Oficial, seções 8.2 (máquina de estados NF-e) e 10
 * (NFC-e/CT-e/MDF-e preparados no domínio sem criar outro core).
 *
 * Nenhum motor fiscal é implementado neste gate. O que existe aqui é o
 * vocabulário do domínio, para que o schema, a API e os testes nasçam
 * alinhados — em particular o estado `unknown_state`, que o Plano exige que
 * seja representável (seção 4.6 do Script Mestre / 8.2 do Plano).
 */

export const FISCAL_DOCUMENT_TYPES = ['nfe', 'nfce', 'nfse', 'cte', 'cteos', 'mdfe'] as const;

export type FiscalDocumentType = (typeof FISCAL_DOCUMENT_TYPES)[number];

/** Modelo fiscal oficial por tipo, quando aplicável. */
export const FISCAL_DOCUMENT_MODEL: Readonly<Partial<Record<FiscalDocumentType, string>>> =
  Object.freeze({
    nfe: '55',
    nfce: '65',
    cte: '57',
    cteos: '67',
    mdfe: '58',
  });

/**
 * Máquina de estados do documento fiscal (Plano Oficial, 8.2).
 *
 * `unknown_state` é estado real e de primeira classe: timeout do autorizador
 * NÃO significa "não emitiu". Retransmissão cega a partir dele é proibida.
 */
export const FISCAL_DOCUMENT_STATES = [
  'draft',
  'validating',
  'ready_to_sign',
  'signed',
  'transmitting',
  'awaiting_response',
  'authorized',
  'rejected',
  'unknown_state',
  'cancel_requested',
  'cancelled',
  'failed_terminal',
] as const;

export type FiscalDocumentState = (typeof FISCAL_DOCUMENT_STATES)[number];

/**
 * Estados a partir dos quais é PROIBIDO retransmitir sem consulta prévia ao
 * autorizador. Consumido pelo Fiscal Core em G3+ e pelo teste de regressão de
 * timeout.
 */
export const STATES_REQUIRING_RECONCILIATION: ReadonlySet<FiscalDocumentState> = new Set([
  'transmitting',
  'awaiting_response',
  'unknown_state',
  'cancel_requested',
]);

/** Estados terminais em que o artefato oficial é imutável. */
export const TERMINAL_STATES: ReadonlySet<FiscalDocumentState> = new Set([
  'authorized',
  'rejected',
  'cancelled',
  'failed_terminal',
]);

export function requiresReconciliationBeforeRetry(state: FiscalDocumentState): boolean {
  return STATES_REQUIRING_RECONCILIATION.has(state);
}

export function isTerminalState(state: FiscalDocumentState): boolean {
  return TERMINAL_STATES.has(state);
}
